import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import removeData from '../../helpers/removeData';

import {
  Avatar,
  Divider,
  Popover,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
} from '@mui/material';
import { AccountCircle as ProfileIcon, ExitToApp as LogoutIcon } from '@mui/icons-material';
import styles from './style.module.css';
import { useLazyLogoutQuery } from '../../app/services/auth';

function PopoverProfile() {
  const [anchorEl, setAnchorEl] = useState(null);

  const { t } = useTranslation();

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const { user } = useAuth();

  function getInitials(user) {
    if (!user || !user.firstname || !user.lastname) {
      return 'U';
    }
    const firstNameInitial = user.firstname.charAt(0).toUpperCase();
    const lastNameInitial = user.lastname.charAt(0).toUpperCase();
    return firstNameInitial + lastNameInitial;
  }

  const [logout] = useLazyLogoutQuery();

  const handleLogout = async () => {
    handleClose();
    try {
      await logout();
      Cookies.remove('token');
      removeData(dispatch);
      navigate('/sign-in');
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div>
      <button onClick={handleClick} className={styles.avatar_button}>
        <Avatar sx={{ bgcolor: 'var(--text-secondary)' }}>{user && getInitials(user)}</Avatar>
      </button>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
              minWidth: 220,
              border: '1px solid rgba(0, 0, 0, 0.08)',
            },
          },
        }}
      >
        <Box sx={{ py: 1 }}>
          {/* User Info Section */}
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="subtitle2" fontWeight={600} color="text.primary">
              {`${user?.firstname} ${user?.lastname}`}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {user?.email}
            </Typography>
          </Box>

          <Divider />

          {/* Menu Items */}
          <List disablePadding>
            <ListItem disablePadding>
              <ListItemButton
                component={NavLink}
                to="/user-profile"
                onClick={handleClose}
                sx={{
                  py: 1.5,
                  '&:hover': {
                    backgroundColor: 'primary.light',
                    color: 'primary.dark',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <ProfileIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={t('generic.profile')}
                  primaryTypographyProps={{ fontSize: '0.875rem' }}
                />
              </ListItemButton>
            </ListItem>

            <ListItem disablePadding>
              <ListItemButton
                onClick={handleLogout}
                sx={{
                  py: 1.5,
                  '&:hover': {
                    backgroundColor: 'error.light',
                    color: 'error.dark',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={t('header.Logout')}
                  primaryTypographyProps={{ fontSize: '0.875rem' }}
                />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </Popover>
    </div>
  );
}

export default PopoverProfile;
